import React, { useCallback, useContext, useMemo, ReactNode } from "react";
import { AuthProvider, AuthProviderProps, useAuth } from "react-oidc-context";
import { User, Log as OIDCLog } from "oidc-client-ts";
import {
  AuthContext,
  AuthContextProviderProps,
  DEFAULT_CONTEXT,
  AuthContextValue,
} from "./AuthContext";

OIDCLog.setLogger(console);
const OIDC_CONFIG: AuthProviderProps = {
  authority: import.meta.env.REACT_APP_IDP_AUTHORITY,
  client_id: import.meta.env.REACT_APP_IDP_CLIENT_ID,
  loadUserInfo: true,
  redirect_uri: window.location.origin + "/authentication/callback/",
  post_logout_redirect_uri: window.location.origin + "/authentication/signout/",
  silent_redirect_uri: window.location.origin + "/authentication/silent/",
  scope: "openid profile applicationapi",
  revokeTokensOnSignout: true,
  automaticSilentRenew: false,
  response_type: "code",
  onSigninCallback: (_user: User | void): void => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

function InnerAuthProvider({ children }: { children: ReactNode }) {
  const {
    user: oidcUser,
    isAuthenticated,
    isLoading,
    error,
    signinRedirect,
    signoutRedirect,
    removeUser,
    activeNavigator,
  } = useAuth();

  const onSignOut = useCallback(() => {
    signoutRedirect({ id_token_hint: oidcUser?.id_token });
    removeUser();
  }, [removeUser, signoutRedirect, oidcUser?.id_token]);

  const value: AuthContextValue = useMemo(
    () => ({
      isAuthenticated,
      user: oidcUser || undefined,
      onSignIn: () => {
        return signinRedirect();
      },
      onSignOut,
      removeUser,
      accessToken: oidcUser?.access_token,
      tokenType: oidcUser?.token_type,
      isLoading,
      error,
      activeNavigator,
    }),
    [
      isAuthenticated,
      oidcUser,
      onSignOut,
      signinRedirect,
      removeUser,
      isLoading,
      error,
      activeNavigator,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function AuthContextProvider({
  children,
}: AuthContextProviderProps) {
  return (
    <AuthProvider {...OIDC_CONFIG}>
      <InnerAuthProvider> {children}</InnerAuthProvider>
    </AuthProvider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    return DEFAULT_CONTEXT;
  }
  return context;
}
